import React from 'react';
import { Link } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { Oval } from 'react-loader-spinner';

import {
  Paper,
  Box,
  Container,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  tableCellClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GppGoodIcon from '@mui/icons-material/GppGood';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { useHistory } from '../../lib/hooks';
import { getBlockExplorer, getTxExplorer, shortenHash } from '../../lib/utils';
import theme, { COLORS } from '../../theme';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#1a202c',
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export const TrackHistory = () => {
  const { address } = useAccount();

  const { data: swapHistory, isFetching } = useHistory(address as string);

  return (
    <Container
      maxWidth="md"
      sx={{
        [theme.breakpoints.down('md')]: {
          px: 0,
        },
      }}
    >
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        sx={{ textAlign: 'center', mb: 3 }}
      >
        Exchange History
      </Typography>
      <TableContainer component={Paper}>
        <Table
          sx={{
            minWidth: 700,
            [theme.breakpoints.down('md')]: {
              minWidth: '100%',
              width: '100%',
            },
          }}
          aria-label="customized table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Transactions</StyledTableCell>
              <StyledTableCell align="right">Details</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {swapHistory &&
              swapHistory.map((his) => (
                <StyledTableRow key={his.transactionHash}>
                  <StyledTableCell width="10px">
                    <Box
                      color={
                        his.status === 'Normal'
                          ? COLORS.fairblock
                          : COLORS.encrypted
                      }
                      display="flex"
                      alignItems="center"
                    >
                      {his.status === 'Encrypted' ? (
                        <HourglassEmptyIcon fontSize="small" />
                      ) : (
                        <TaskAltIcon fontSize="small" />
                      )}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    <Link
                      to={getTxExplorer(his.transactionHash)}
                      style={{
                        textDecoration: 'none',
                        color: 'white',
                      }}
                      target="_blank"
                    >
                      <Box
                        sx={{
                          ':hover': {
                            color:
                              his.status === 'Normal'
                                ? COLORS.fairblock
                                : COLORS.encrypted,
                          },
                          display: 'flex',
                        }}
                        gap={1}
                      >
                        {shortenHash(his.transactionHash, 10)}{' '}
                        <OpenInNewIcon fontSize="small" />
                      </Box>
                    </Link>
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Box>
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        color="#bbb"
                        fontSize="12px"
                      >
                        Block Number: &nbsp;
                        <Link
                          to={getBlockExplorer(his.blockNumber)}
                          style={{
                            textDecoration: 'none',
                            color: 'white',
                          }}
                          target="_blank"
                        >
                          <Box
                            sx={{
                              ':hover': {
                                color:
                                  his.status === 'Normal'
                                    ? COLORS.fairblock
                                    : COLORS.encrypted,
                              },
                            }}
                          >
                            {his.blockNumber}
                          </Box>
                        </Link>
                      </Box>
                      <Box
                        color="#bbb"
                        fontSize="12px"
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        Type: &nbsp;
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          style={{ color: 'white' }}
                        >
                          {his.status}{' '}
                          {his.status === 'Encrypted' && (
                            <Box color={COLORS.encrypted} display="flex">
                              <GppGoodIcon fontSize="small" />
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <Box
                        color="#bbb"
                        fontSize="12px"
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        Status: &nbsp;
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ color: 'white' }}
                        >
                          {his.status === 'Encrypted' && 'Pending'}
                          {his.status !== 'Encrypted' && 'Success'}
                        </Box>
                      </Box>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {swapHistory && swapHistory.length <= 0 && (
        <Box p={5} textAlign="center">
          No Transactions Found!
        </Box>
      )}
      {isFetching && (
        <Box p={5} display="flex" justifyContent="center">
          <Oval
            visible={true}
            height="24"
            width="24"
            color={COLORS.fairblock}
            ariaLabel="oval-loading"
            strokeWidthSecondary={10}
            strokeWidth={10}
            secondaryColor="white"
          />
        </Box>
      )}
    </Container>
  );
};
